import { useDisclosure } from '@chakra-ui/react';
import { AccountingPeriodLockHistoryDrawer } from './AccountingPeriodLockHistoryDrawer';
import { format as formatDate } from 'date-fns/format';
import { getMonth } from 'date-fns/getMonth';
import { getYear } from 'date-fns/getYear';
import { parseISO } from 'date-fns/parseISO';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdOutlineEdit } from 'react-icons/md';
import {
  useCreateAccountingPeriodLock,
  useGetActivePeriodLock,
} from '../../api/revenueService';
import {
  MButton,
  MCustomIconButton,
  MDivider,
  MFlex,
  MFormField,
  MTooltip,
} from '../../components/Monetize';
import { DatePicker } from '../../components/Monetize/DatePicker/DatePicker';
import MPopoverWrapper from '../../components/Monetize/MPopoverWrapper';
import { TitleValuePair } from '../../components/Monetize/TitleValuePair';

interface LockFormValues {
  lockDate: string;
}

function lockToDateString(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}-01`;
}

function dateStringToLock(iso: string): { year: number; month: number } {
  const parsed = parseISO(iso);
  return { year: getYear(parsed), month: getMonth(parsed) + 1 };
}

function formatPeriod(year: number, month: number): string {
  return formatDate(new Date(year, month - 1, 1), 'MMMM yyyy');
}

export const AccountingPeriodLockHeader = () => {
  const { open: isOpen, onOpen, onClose } = useDisclosure();

  const { data: activeLock, isLoading } = useGetActivePeriodLock({
    meta: { showErrorToast: true },
  });

  const { mutate: createLock, isPending: isSaving } =
    useCreateAccountingPeriodLock({
      meta: { showErrorToast: true },
      onSuccess: () => {
        onClose();
      },
    });

  const {
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<LockFormValues>({
    mode: 'onChange',
    defaultValues: { lockDate: '' },
  });

  const currentLockDate = watch('lockDate');

  const activeLockDateString = activeLock
    ? lockToDateString(activeLock.year, activeLock.month)
    : '';

  // Keep the form in sync with the active lock whenever it changes or the
  // popover is (re)opened, so edits always start from the current value.
  useEffect(() => {
    reset({ lockDate: activeLockDateString });
  }, [activeLockDateString, reset, isOpen]);

  const handleSave = ({ lockDate }: LockFormValues) => {
    createLock(dateStringToLock(lockDate));
  };

  const displayValue = activeLock
    ? formatPeriod(activeLock.year, activeLock.month)
    : 'Not set';

  return (
    <MFlex alignItems="center" columnGap={2}>
      <MTooltip
        label="Periods up to and including this month are locked for editing."
        placement="top"
      >
        <MFlex flexDirection="column" data-testid="active-lock-period">
          <TitleValuePair
            title="Current Rev Rec Lock Period"
            value={isLoading ? '—' : displayValue}
          />
        </MFlex>
      </MTooltip>
      <AccountingPeriodLockHistoryDrawer />
      <MPopoverWrapper
        showArrow
        usePortal
        showCloseBtn
        isOpen={isOpen}
        placement="bottom-end"
        onOpen={onOpen}
        onClose={onClose}
        triggerContent={
          <MCustomIconButton
            icon={MdOutlineEdit}
            aria-label="Edit rev rec lock period"
            data-testid="edit-lock-period-button"
            isDisabled={isLoading}
          />
        }
        headerContent="Accounting Periods"
        bodyContent={
          <form onSubmit={handleSubmit(handleSave)}>
            <MFormField
              error={errors?.lockDate}
              label="Current Rev Rec Lock Period"
              labelProps={{ mb: 2 }}
              tooltip="Periods up to and including this month are locked for editing."
            >
              <Controller
                name="lockDate"
                control={control}
                render={({ field: { onChange, value, ...rest } }) => (
                  <DatePicker
                    mode="MONTH"
                    value={value ?? ''}
                    placeholder="Select month"
                    onChange={(dateString) => {
                      onChange(dateString);
                    }}
                    useCustomFormatting
                    {...rest}
                  />
                )}
              />
            </MFormField>
            <MDivider my={2} />
            <MFlex justifyContent="flex-end">
              <MButton
                type="submit"
                isLoading={isSaving}
                isDisabled={
                  isSaving ||
                  !currentLockDate ||
                  activeLockDateString === currentLockDate
                }
              >
                Save
              </MButton>
            </MFlex>
          </form>
        }
      />
    </MFlex>
  );
};
